import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Card,
  CardContent,
  Grid,
  Divider
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import TimerIcon from '@mui/icons-material/Timer';
import PhoneIcon from '@mui/icons-material/Phone';
import PersonIcon from '@mui/icons-material/Person';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import api from '../axios';
import ModuleHeader from '../components/ModuleHeader';
import Footer from '../components/Footer';

const RelatoriosConsolidacao = ({ onLogout }) => {
  const [distribuicoesAtrasadas, setDistribuicoesAtrasadas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [estatisticas, setEstatisticas] = useState({
    total_visitantes: 0,
    total_consolidadores: 0,
    total_distribuicoes: 0,
    taxa_contato: 0,
    tempo_medio_resposta: 0,
    distribuicoes_ativas: 0,
    distribuicoes_atrasadas: 0
  });

  useEffect(() => {
    Promise.all([
      fetchDistribuicoesAtrasadas(),
      fetchEstatisticas()
    ]);
  }, []);

  const fetchDistribuicoesAtrasadas = async () => {
    try {
      const response = await api.get('/distribuicoes/atrasadas');
      setDistribuicoesAtrasadas(response.data.distribuicoes || []);
    } catch (error) {
      console.error('Erro ao buscar distribuições atrasadas:', error);
      setError('Erro ao carregar distribuições atrasadas');
    } finally {
      setLoading(false);
    }
  };

  const fetchEstatisticas = async () => {
    try {
      // Buscar dados para calcular estatísticas
      const [visitantesRes, consolidadoresRes, distribuicoesAtraRes] = await Promise.all([
        api.get('/visitantes/'),
        api.get('/consolidadores/'),
        api.get('/distribuicoes/atrasadas')
      ]);

      const visitantes = visitantesRes.data;
      const consolidadores = consolidadoresRes.data;
      const atrasadas = distribuicoesAtraRes.data.distribuicoes || [];

      const total_visitantes = visitantes.length;
      const total_consolidadores = consolidadores.length;
      const distribuidos = visitantes.filter(v => v.distribuido).length;
      const contatados = visitantes.filter(v => v.status_contato !== 'nao_contactado').length;
      const taxa_contato = distribuidos > 0 ? ((contatados / distribuidos) * 100).toFixed(1) : 0;

      setEstatisticas({
        total_visitantes,
        total_consolidadores,
        total_distribuicoes: distribuidos,
        taxa_contato,
        tempo_medio_resposta: 0, // Calcular se tiver dados de tempo
        distribuicoes_ativas: distribuidos - atrasadas.length,
        distribuicoes_atrasadas: atrasadas.length
      });
    } catch (error) {
      console.error('Erro ao buscar estatísticas:', error);
    }
  };

  const formatarData = (dataISO) => {
    if (!dataISO) return '-';
    const data = new Date(dataISO);
    return data.toLocaleString('pt-BR');
  };

  const calcularTempoAtraso = (dataLimite) => {
    const agora = new Date();
    const limite = new Date(dataLimite);
    const diff = agora - limite;
    
    const horas = Math.floor(diff / (1000 * 60 * 60));
    const dias = Math.floor(horas / 24);
    
    if (dias > 0) {
      return `${dias} dia${dias > 1 ? 's' : ''} atrasado`;
    }
    return `${horas}h atrasado`;
  };

  return (
    <>
      <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>
        <ModuleHeader module="Consolidação" title="Relatórios de Consolidação" />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Cards de Estatísticas Gerais */}
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          Visão Geral do Sistema
        </Typography>
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" variant="caption">
                      Total de Visitantes
                    </Typography>
                    <Typography variant="h4" color="primary.main" sx={{ fontSize: '4.4rem', fontWeight: 800, lineHeight: 1 }}>{estatisticas.total_visitantes}</Typography>
                  </Box>
                  <PersonIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" variant="caption">
                      Consolidadores Ativos
                    </Typography>
                    <Typography variant="h4" color="primary.main" sx={{ fontSize: '4.4rem', fontWeight: 800, lineHeight: 1 }}>{estatisticas.total_consolidadores}</Typography>
                  </Box>
                  <PersonIcon sx={{ fontSize: 48, color: 'success.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" variant="caption">
                      Distribuições Ativas
                    </Typography>
                    <Typography variant="h4" color="primary.main" sx={{ fontSize: '4.4rem', fontWeight: 800, lineHeight: 1 }}>
                      {estatisticas.distribuicoes_ativas}
                    </Typography>
                  </Box>
                  <TimerIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" variant="caption">
                      Taxa de Contato
                    </Typography>
                    <Typography variant="h4" color="primary.main" sx={{ fontSize: '4.4rem', fontWeight: 800, lineHeight: 1 }}>
                      {estatisticas.taxa_contato}%
                    </Typography>
                  </Box>
                  <TrendingUpIcon sx={{ fontSize: 48, color: 'success.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Alertas de Distribuições Atrasadas */}
        <Box display="flex" alignItems="center" gap={2} mb={2} sx={{ flexWrap: 'wrap' }}>
          <Typography variant="h6">
            Distribuições Atrasadas (Prazo 48h Vencido)
          </Typography>
          <Chip
            label={`${estatisticas.distribuicoes_atrasadas} atrasadas`}
            color="error"
            icon={<WarningIcon />}
          />
        </Box>

        {distribuicoesAtrasadas.length === 0 ? (
          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography variant="body1">
              🎉 Excelente! Não há distribuições atrasadas no momento.
            </Typography>
          </Alert>
        ) : (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body1">
              ⚠️ Atenção: {distribuicoesAtrasadas.length} distribuição(ões) com prazo vencido.
              Os consolidadores precisam ser notificados!
            </Typography>
          </Alert>
        )}

        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Visitante</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Consolidador</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Contato</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Data Distribuição</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Prazo Limite</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Tempo Atraso</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {distribuicoesAtrasadas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body1" color="text.secondary" sx={{ py: 4 }}>
                      Nenhuma distribuição atrasada 🎉
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                distribuicoesAtrasadas.map((dist) => (
                  <TableRow
                    key={dist.distribuicao_id}
                    hover
                  >
                    <TableCell>
                      <strong>{dist.visitante_nome}</strong>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <PersonIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {dist.consolidador_nome}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" flexDirection="column" gap={0.5}>
                        {dist.visitante_telefone && (
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <PhoneIcon fontSize="small" color="action" />
                            <Typography variant="caption">
                              {dist.visitante_telefone}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {formatarData(dist.data_distribuicao)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="error.main" fontWeight="bold">
                        {formatarData(dist.data_limite)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={calcularTempoAtraso(dist.data_limite)}
                        size="small"
                        color="error"
                        icon={<WarningIcon />}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider sx={{ my: 4 }} />

        {/* Estatísticas por Consolidador */}
        <Typography variant="h6" gutterBottom>
          Desempenho dos Consolidadores
        </Typography>
        
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            📊 Em breve: estatísticas detalhadas de desempenho por consolidador,
            incluindo tempo médio de resposta, taxa de sucesso e total de visitantes contatados.
          </Typography>
        </Alert>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              Métricas do Sistema
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box display="flex" justifyContent="space-between" alignItems="center" py={1}>
                  <Typography variant="body2" color="text.secondary">
                    Total de Visitantes Cadastrados:
                  </Typography>
                  <Typography variant="h6">{estatisticas.total_visitantes}</Typography>
                </Box>
                <Divider />
                <Box display="flex" justifyContent="space-between" alignItems="center" py={1}>
                  <Typography variant="body2" color="text.secondary">
                    Total de Distribuições:
                  </Typography>
                  <Typography variant="h6">{estatisticas.total_distribuicoes}</Typography>
                </Box>
                <Divider />
                <Box display="flex" justifyContent="space-between" alignItems="center" py={1}>
                  <Typography variant="body2" color="text.secondary">
                    Taxa de Contato:
                  </Typography>
                  <Typography variant="h6" color="success.main">
                    {estatisticas.taxa_contato}%
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box display="flex" justifyContent="space-between" alignItems="center" py={1}>
                  <Typography variant="body2" color="text.secondary">
                    Consolidadores Ativos:
                  </Typography>
                  <Typography variant="h6">{estatisticas.total_consolidadores}</Typography>
                </Box>
                <Divider />
                <Box display="flex" justifyContent="space-between" alignItems="center" py={1}>
                  <Typography variant="body2" color="text.secondary">
                    Distribuições Ativas:
                  </Typography>
                  <Typography variant="h6" color="primary.main">
                    {estatisticas.distribuicoes_ativas}
                  </Typography>
                </Box>
                <Divider />
                <Box display="flex" justifyContent="space-between" alignItems="center" py={1}>
                  <Typography variant="body2" color="text.secondary">
                    Distribuições Atrasadas:
                  </Typography>
                  <Typography variant="h6" color="error.main">
                    {estatisticas.distribuicoes_atrasadas}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Box display="flex" justifyContent="center" gap={2} mt={4} sx={{ flexDirection: { xs: 'column', md: 'row' } }}>
          <Button
            variant="outlined"
            onClick={() => window.location.reload()}
          >
            Atualizar Dados
          </Button>
        </Box>
      </Container>
      <Footer />
    </>
  );
};

export default RelatoriosConsolidacao;
